import {
  FilterSelectedTags,
  LoadingListCard,
  VenueCard,
} from "Explore/Components";
import { searchVenues } from "Explore/Services/Venue";
import { Alert, Tag, Empty } from "antd";
import { useEffect, useRef, useState } from "react";
import { FiltersVenueMobile } from "./Components";
import styles from "./VenueList.module.scss";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { ParamsFilterKey } from "Config/filterSearchParams";
import FiltersVenue from "./Components/FiltersVenue";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { Grid } from "@mui/material";
import SuggestedKeywords from "Explore/Components/SuggestedKeywords/SuggestedKeywords";
import { getRelatedKeywords } from "Explore/Services/Common";
import { getExampleConceptExplore } from "Explore/Services/Concept";
import CallToLogin from "Explore/Components/CallToLogin";
import FeatureEntities from "Explore/Components/FeatureEntities";
import classNames from "classnames";
import KeywordClickSnackBar from "Explore/Components/KeywordClickSnackBar";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useAuth from "Hooks/useAuth";
import LoginRequiredSection from "Explore/Components/LoginRequired";
import InfinityPagination from "Explore/Components/Pagination/InfinityPagination";
import useInfinityPagination from "Hooks/useInfinityPagination";

const VenueList = () => {
  const { userTypeConfig } = useAuth({ entityType: "VENUE" });
  const { isSmallDesktop, isTablet } = useDeviceCheck();
  const { filter, updateSearchParams, pagination, getPageHref } =
    useFilterAndPagination();
  const [suggestedKeywords, setSuggestedKeywords] = useState({
    data: [],
    isLoading: true,
  });
  const [showLoginSection, setShowLoginSection] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [keywordsUsedForSearch, setKeywordsUsedForSearch] = useState([]);
  const scrollToRef = useRef(null);
  const { keyword } = useParams();
  const titleKeyword =
    filter?.[ParamsFilterKey.KEYWORD_QUERY]?.[0] ||
    filter?.[ParamsFilterKey.QUERY]?.[0] ||
    keyword;

  const {
    data: listData,
    currentMaxPage,
    hasNextPage,
    isLoading,
  } = useInfinityPagination({
    fetchFunction: searchVenues,
    page: pagination.page,
    filter,
    rowsPerPage: pagination.rowsPerPage,
    setPagination: updateSearchParams,
    setKeywordsUsedForSearch,
    errCallback: (e) => {
      if (e?.response?.status === 401) {
        setShowLoginSection(true);
      }
    },
  });

  const pageToCompute = pagination.page - 1;

  const numPagesPerFetch = userTypeConfig?.limit?.numPagesPerFetch || 1;

  const showInformationNotesAboutKeywordUsedToSearch =
    keywordsUsedForSearch.length > 1;

  const informationNotesAboutKeywordUsedToSearchDisplayMessage = (
    <>
      <p>
        Sorry! We do not have <b>{filter.keywordQuery}</b> in our keyword
        glossary. Instead, we are using the following keywords to search
      </p>
      <div>
        {keywordsUsedForSearch.map((kw, index) => (
          <Tag key={index}>{kw}</Tag>
        ))}
      </div>
    </>
  );

  useEffect(() => {
    if (keyword) {
      updateSearchParams({
        [ParamsFilterKey.QUERY]: [keyword],
      });
    }
  }, [keyword]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [pagination.page]);

  // Reset the keyword note and reload suggested keywords when the filter changes.
  useEffect(() => {
    setKeywordsUsedForSearch([]);
    setSuggestedKeywords({ data: [], isLoading: true });

    if (filter[ParamsFilterKey.KEYWORD_QUERY]?.length > 0) {
      getRelatedKeywords({
        keywords: filter[ParamsFilterKey.KEYWORD_QUERY],
        entity: "venues",
      }).then((res) => {
        setSuggestedKeywords({ data: res, isLoading: false });
      });
    } else {
      getExampleConceptExplore(0, 25, (res) => {
        setSuggestedKeywords({ data: res.results, isLoading: false });
      });
    }
  }, [JSON.stringify(filter)]);

  const handleSuggestedKeywordClick = (keywords) => {
    const filterKeywords = [...(filter[ParamsFilterKey.KEYWORD_QUERY] || [])];
    keywords.forEach((keyword) => {
      if (!filterKeywords.includes(keyword)) {
        filterKeywords.push(keyword);
      }
    });

    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: filterKeywords,
    });
  };

  const onKeywordClick = (keyword) => {
    const _filter = filter?.[[ParamsFilterKey.KEYWORD_QUERY]] || [];
    if (_filter.includes(keyword)) {
      setSnackBarOpen(true);
      return;
    }

    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: [..._filter, keyword],
    });
  };

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

  const generateTitle = () => {
    // Advanced title logic (commented out for future version)

    if (!titleKeyword) return "Search Venues | Academic Online";
    const suffix = " | Academic Online";
    const maxLength = 65;

    let titleFull = `Top Venues in ${titleKeyword}`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    let titleShort = `${titleKeyword} Venues`;
    if ((titleShort + suffix).length <= maxLength) return titleShort + suffix;

    return `${titleKeyword.substring(0, 40)}...${suffix}`;

    // Original simple logic
    // return "Explore Academic Venues & Event Spaces | Academic Explorer";
  };

  const generateDescription = () => {
    return titleKeyword
      ? `Discover top academic venues, conferences, and journals related to ${titleKeyword}.`
      : "Browse academic venues across different locations. Find the ideal space for conferences, seminars, and academic events.";
  };

  return (
    <>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      <div className={styles["venue-list"]}>
        {!isSmallDesktop && <FiltersVenue />}

        <div className={styles["results"]}>
          <div className={styles["results__header"]}>
            <h1 className={styles["results--title"]}>
              Explore Spaces for Academic Events
            </h1>
            <p className={styles["results--description"]}>
              Browse{" "}
              <b>
                <em>academic venues</em>
              </b>{" "}
              across different locations, making it easy to find the ideal space
              for your event{" "}
            </p>
          </div>
          <div className={styles["results__body"]}>
            <SuggestedKeywords
              keywords={suggestedKeywords}
              onKeywordClick={handleSuggestedKeywordClick}
            />

            {isSmallDesktop && (
              <div className={styles["results--controller"]}>
                <FiltersVenueMobile />
              </div>
            )}

            <FilterSelectedTags
              showLabel
              filters={filter}
              setFilters={updateSearchParams}
            />
            {/* information notes about using keywords */}
            {showInformationNotesAboutKeywordUsedToSearch && (
              <>
                <Alert
                  message={
                    informationNotesAboutKeywordUsedToSearchDisplayMessage
                  }
                  type="info"
                  showIcon
                />
                <br />
              </>
            )}

            <div className={styles["venue-list__cards"]} ref={scrollToRef}>
              <LoadingListCard
                isLoading={isLoading}
                grid={{
                  gutter: 25,
                  column: 1,
                }}
                componentNum={15}
                component={
                  Array.isArray(listData[pageToCompute]) &&
                  listData[pageToCompute].length > 0 ? (
                    <div className={styles["content-venue"]}>
                      <Grid container spacing="25px">
                        {listData[pageToCompute].map((e, i) => (
                          <Grid item key={i} xs={12}>
                            <VenueCard
                              data={e}
                              className={classNames(
                                styles["card"],
                                i % 2 === 0
                                  ? styles["even-card"]
                                  : styles["odd-card"],
                              )}
                              onKeywordClick={onKeywordClick}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  ) : showLoginSection ? (
                    <LoginRequiredSection />
                  ) : (
                    <Empty description="No Venues Found" />
                  )
                }
              />
              <InfinityPagination
                pagination={pagination}
                setPagination={updateSearchParams}
                sizeChanger={userTypeConfig?.ui_components?.pageSizeChanger ?? true}
                pageSizeOptions={[15, 30, 45, 60]}
                currentMaxPage={currentMaxPage}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                addLink
                getPageHref={getPageHref}
                numPagesPerFetch={numPagesPerFetch}
              />
            </div>
          </div>
        </div>
        <div className={styles["right-section"]}>
          {!isTablet && <CallToLogin />}
          <FeatureEntities />
        </div>
      </div>
      <KeywordClickSnackBar open={snackBarOpen} setOpen={setSnackBarOpen} />
    </>
  );
};

export default VenueList;
